import { Injectable, effect, inject, linkedSignal } from '@angular/core';
import { WINDOW } from '@tvd/cdk/browser';

export const CONSENT_KEY = 'analytics_consent';

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  readonly #winRef = inject(WINDOW);

  readonly #hasConsent = linkedSignal(() => this.#readConsentCookie());
  readonly hasConsent = this.#hasConsent.asReadonly();

  constructor() {
    effect(() => {
      const consent = this.#hasConsent();
      if (consent !== null) {
        this.#writeConsentCookie(consent);
      }
    });
  }

  acceptAnalytics(): void {
    this.#hasConsent.set(true);
  }

  rejectAnalytics(): void {
    this.#hasConsent.set(false);
  }

  #getRootDomain(): string {
    const parts = this.#winRef.location.hostname.split('.');
    return parts.length > 1
      ? `.${parts.slice(-2).join('.')}`
      : this.#winRef.location.hostname;
  }

  #readConsentCookie(): boolean | null {
    try {
      const match = this.#winRef.document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${CONSENT_KEY}=`));
      if (!match) return null;
      return JSON.parse(decodeURIComponent(match.split('=')[1])) as boolean;
    } catch {
      return null;
    }
  }

  #writeConsentCookie(consent: boolean): void {
    const maxAge = 365 * 24 * 60 * 60; // 1 year
    const domain = this.#getRootDomain();
    this.#winRef.document.cookie = `${CONSENT_KEY}=${encodeURIComponent(JSON.stringify(consent))}; path=/; domain=${domain}; max-age=${maxAge}; SameSite=Lax; Secure`;
  }

  /**
   * Clear all cookies except for essential ones like consent
   */
  static clearAllCookies(exceptions: string[] = [CONSENT_KEY]): void {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Skip exceptions (like consent cookies)
      if (exceptions.includes(name)) {
        continue;
      }

      // Clear cookie for current domain and path
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;

      // Clear cookie for current domain and root path
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;

      // Clear cookie for parent domain (in case of subdomains)
      const domainParts = window.location.hostname.split('.');
      if (domainParts.length > 1) {
        const parentDomain = `.${domainParts.slice(-2).join('.')}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain};`;
      }
    }
  }
}
